import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getErrorMessage } from 'shared/utils/errorMessage';
import { getRoleDetail } from 'features/management/api/getRoleDetail';
import useRoleMenus from './useRoleMenus';
import useUpsertRole from './useUpsertRole';

const buildFormDataFromMenus = (availableMenus) =>
  availableMenus.flatMap((menu) => {
    if (menu.child_menu) {
      return menu.child_menu.map((child) => ({
        menu_id: child.menu_id,
        can_create: child.can_create,
        can_read: child.can_read,
        can_update: child.can_update,
        can_delete: child.can_delete,
        can_approve: child.can_approve,
        can_read_all: child.can_read_all,
      }));
    }
    return [
      {
        menu_id: menu.menu_id,
        can_create: menu.can_create,
        can_read: menu.can_read,
        can_update: menu.can_update,
        can_delete: menu.can_delete,
        can_approve: menu.can_approve,
        can_read_all: menu.can_read_all,
      },
    ];
  });

const mergeRoleDetailIntoForm = (formData, roleMenus) =>
  formData.map((menu) => {
    const found = roleMenus?.find((rm) => rm.id === menu.menu_id);
    if (!found) return { ...menu };
    return {
      menu_id: found.id,
      can_create: found.can_create,
      can_read: found.can_read,
      can_update: found.can_update,
      can_delete: found.can_delete,
      can_approve: found.can_approve,
      can_read_all: found.can_read_all,
    };
  });

/**
 * Composer hook for the role create/edit form.
 * Composes useRoleMenus (data) + local form state + useUpsertRole (mutation).
 *
 * @param {{ id?: string | number }} options
 */
export const useRoleForm = ({ id } = {}) => {
  const navigate = useNavigate();
  const { availableMenus, menus, isLoadingMenus, refetchMenus } =
    useRoleMenus();

  const [roleName, setRoleName] = useState('');
  const [formData, setFormData] = useState([]);
  const [errors, setErrors] = useState();
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [hideMenus, setHideMenus] = useState([]);
  const [hideMenuChild, setHideMenuChild] = useState([]);

  // Build initial form data when menus arrive.
  useEffect(() => {
    if (availableMenus.length === 0) return;
    setFormData(buildFormDataFromMenus(availableMenus));
  }, [availableMenus]);

  // Load role detail when editing — merges existing permissions into form.
  useEffect(() => {
    if (!id || availableMenus.length === 0) return;

    let cancelled = false;
    const loadDetail = async () => {
      setIsLoadingDetail(true);
      try {
        const res = await getRoleDetail(id);
        if (cancelled) return;
        if (res?.meta?.http_status === 200 && res.data) {
          setRoleName(res.data?.role);
          const baseForm = buildFormDataFromMenus(availableMenus);
          setFormData(mergeRoleDetailIntoForm(baseForm, res.data.menus));
        } else {
          toast.error(
            res?.response?.data?.errors?.[0]?.message || res?.message
          );
        }
      } catch (error) {
        if (!cancelled) toast.error(getErrorMessage(error));
      } finally {
        if (!cancelled) setIsLoadingDetail(false);
      }
    };

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [id, availableMenus]);

  const { upsertMutation } = useUpsertRole({
    id,
    onSuccess: () => navigate('/management-role'),
  });

  const handleCheckedParent = useCallback((target, val) => {
    setFormData((prev) =>
      prev.map((menu) =>
        val.child_menu?.find((child) => child.menu_id === menu.menu_id)
          ? {
              ...menu,
              can_create: target.checked,
              can_read: target.checked,
              can_update: target.checked,
              can_delete: target.checked,
              can_approve: target.checked,
              can_read_all: target.checked,
            }
          : menu
      )
    );
  }, []);

  const handleChecked = useCallback((checked, item, type) => {
    setFormData((prev) => {
      const menuIndex = prev.findIndex(
        ({ menu_id }) => menu_id === item.menu_id
      );
      if (menuIndex === -1) return prev;

      return prev.map((menu, i) => {
        if (i !== menuIndex) return menu;
        return {
          ...menu,
          can_create:
            type === 'all' || type === 'can_create' ? checked : menu.can_create,
          can_read:
            type === 'all' || type === 'can_read' || type === 'can_read_all'
              ? checked
              : menu.can_read,
          can_update:
            type === 'all' || type === 'can_update' ? checked : menu.can_update,
          can_delete:
            type === 'all' || type === 'can_delete' ? checked : menu.can_delete,
          can_approve:
            type === 'all' || type === 'can_approve'
              ? checked
              : menu.can_approve,
          can_read_all:
            type === 'all' || type === 'can_read_all'
              ? checked
              : menu.can_read_all,
        };
      });
    });
  }, []);

  const toggleMenu = useCallback((index, child = false) => {
    if (child) {
      setHideMenuChild((prev) =>
        prev.includes(index)
          ? prev.filter((i) => i !== index)
          : [...prev, index]
      );
    } else {
      setHideMenus((prev) =>
        prev.includes(index)
          ? prev.filter((i) => i !== index)
          : [...prev, index]
      );
    }
  }, []);

  const onSubmit = () => {
    if (roleName === '') {
      setErrors({ role: 'Role Name cannot be empty' });
      toast.error('Role Name cannot be empty');
      return;
    }

    const hasMenuSelected = formData.some(
      (item) =>
        item.can_create || item.can_delete || item.can_read || item.can_update
    );

    if (!hasMenuSelected) {
      setErrors({ menus: 'Please select menu access' });
      toast.error('Please select menu access');
      return;
    }

    upsertMutation.mutate({ role: roleName, menus: formData });
  };

  return {
    availableMenus,
    menus,
    roleName,
    formData,
    errors,
    isLoadingDetail,
    isLoadingMenus,
    isSubmited: upsertMutation.isLoading,
    hideMenus,
    hideMenuChild,
    setRoleName,
    setErrors,
    handleChecked,
    handleCheckedParent,
    toggleMenu,
    onSubmit,
    refetchMenus,
  };
};
